import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import {
  assignTickets,
  bulkFormQuery,
  mergeTickets,
  suspendTickets,
  type BulkForm,
  type BulkFormKind,
  type BulkFormTicket,
} from '../api/easeedesk'
import { Modal } from '../ui/Modal'
import { SelectPicker } from '../ui/SelectPicker'
import { SettingsCheck } from '../ui/SettingsParts'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import './TicketActionForm.css'

/**
 * Ferestrele meniului "Actions" de pe lista de tichete: Merge, Suspend service,
 * Assign to Operator - `#mergeModal`, `#suspendServiceModal`,
 * `#assignOperatorModal` din `public/js/easeedesk/easeedesk.js`, cu machetele
 * din `tickets/merge/form.php`, `tickets/planning_tickets/suspend_service_form.php`
 * si `tickets/assign_to_operator/form.php`.
 *
 * Fiecare fereastra cere INTAI serverului ce afla si portalul la `show.bs.modal`
 * (tichetele bifate, erorile, listele) - `GET tickets/bulk-form/{kind}` - si
 * abia apoi deseneaza. Aceleasi trei lucruri, in aceeasi ordine ca in machete:
 * erorile sus, campurile, "Tickets" cu cate un rand pe tichet si butonul rosu
 * de scos (`.remove-merge-ticket` si fratii lui), butoanele jos la dreapta.
 *
 * Campurile stau pe grila comuna `.tk-action-form` (eticheta pe prima coloana,
 * campul pe a doua), ca toate formularele de tichet din v4.
 */

const TITLE: Record<BulkFormKind, string> = {
  merge: 'Merge tickets',
  suspend: 'Suspend service',
  assign: 'Assign operator',
}

const MODAL_ID: Record<BulkFormKind, string> = {
  merge: 'mergeModal',
  suspend: 'suspendServiceModal',
  assign: 'assignOperatorModal',
}

export function TicketBulkForms({
  kind,
  ids,
  onClose,
  onDone,
}: {
  kind: BulkFormKind
  ids: number[]
  onClose(): void
  /** Dupa scriere: lista se redeseneaza si bifele se sterg. */
  onDone(): void
}) {
  const form = useQuery(bulkFormQuery(kind, ids))

  return (
    <Modal id={MODAL_ID[kind]} open onClose={onClose} title={TITLE[kind]} labelledBy={`${MODAL_ID[kind]}Label`}>
      {form.isPending && <Spinner />}
      {form.isError && <Errors errors={[form.error instanceof Error ? form.error.message : 'Something went wrong']} />}
      {form.isSuccess && kind === 'merge' && <MergeForm form={form.data} onClose={onClose} onDone={onDone} />}
      {form.isSuccess && kind === 'suspend' && <SuspendForm form={form.data} onClose={onClose} onDone={onDone} />}
      {form.isSuccess && kind === 'assign' && <AssignForm form={form.data} onClose={onClose} onDone={onDone} />}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/*  MERGE - `tickets/merge/form.php`                                          */
/* -------------------------------------------------------------------------- */

function MergeForm({ form, onClose, onDone }: { form: BulkForm; onClose(): void; onDone(): void }) {
  const [tickets, setTickets] = useState<BulkFormTicket[]>(form.tickets)
  const [parent, setParent] = useState('')
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState(false)
  const [saving, setSaving] = useState(false)

  const available = form.available ?? []

  /* `select.selected-merge-ticket` change: subiectul parintelui se copiaza in
     casuta, de unde omul il poate schimba. */
  const pickParent = (value: string) => {
    setParent(value)
    const chosen = available.find((t) => String(t.ID) === value)
    setSubject(chosen ? chosen.subject : '')
  }

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (saving || tickets.length === 0 || parent === '') return
    setSaving(true)
    mergeTickets(Number(parent), tickets.map((t) => t.ID), subject, description)
      .then(() => {
        toast('Tickets merged successfully.', 'Merge tickets', 'success')
        onDone()
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Something went wrong', 'Merge tickets', 'error'))
      .finally(() => setSaving(false))
  }

  return (
    <form id="merge_tickets_form" className="tk-action-form tk-bulk-form" onSubmit={submit}>
      <div className="alert alert-info">
        <i className="fas fa-info-circle" aria-hidden="true"></i> Tickets must have the same company and type
      </div>
      <Errors errors={form.errors} />

      <div className="form-group">
        <label htmlFor="merge-parent" className="control-label">
          Merge ticket <span className="required">*</span>
        </label>
        <SelectPicker
          id="merge-parent"
          className="form-control selected-merge-ticket"
          title="Select ticket..."
          options={available.map((t) => ({ value: String(t.ID), label: `[#${t.ID}] ${t.subject}` }))}
          value={parent}
          onChange={pickParent}
        />
      </div>

      <div className="form-group">
        <label htmlFor="merge-subject" className="control-label">
          Merge ticket subject <span className="required">*</span>
        </label>
        <input
          id="merge-subject"
          className="form-control selected-merge-ticket-subject"
          required
          type="text"
          placeholder="Select merge ticket"
          value={subject}
          onChange={(ev) => setSubject(ev.target.value)}
        />
      </div>

      <div className="form-group">
        <label className="control-label">Merge fields</label>
        <div className="checkbox">
          <SettingsCheck className="merge-fields" checked={description} onChange={setDescription} label="Description" />
        </div>
      </div>

      <hr />

      <TicketRows
        label="Tickets to merge"
        rowClass="ticket-to-merge"
        buttonClass="remove-merge-ticket"
        tickets={tickets}
        onRemove={(id) => setTickets((was) => was.filter((t) => t.ID !== id))}
        emptyText="Please select tickets to merge"
      />

      <Buttons saveClass="save-merge" disabled={saving || tickets.length === 0 || form.errors.length > 0 || parent === ''} onClose={onClose} />
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/*  SUSPEND SERVICE - `planning_tickets/suspend_service_form.php`             */
/* -------------------------------------------------------------------------- */

function SuspendForm({ form, onClose, onDone }: { form: BulkForm; onClose(): void; onDone(): void }) {
  const [tickets, setTickets] = useState<BulkFormTicket[]>(form.tickets)
  const [when, setWhen] = useState('')
  const [comments, setComments] = useState('')
  const [saving, setSaving] = useState(false)

  /* Serviciul stins sau nimic bifat: macheta arata DOAR erorile, fara formular. */
  if (form.errors.length > 0) return <Errors errors={form.errors} />

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (saving || tickets.length === 0 || when === '') return
    setSaving(true)
    suspendTickets(
      tickets.map((t) => t.ID),
      new Date(when),
      comments || undefined,
    )
      .then(() => {
        toast('Tickets planned successfully.', 'Planning tickets', 'success')
        onDone()
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Something went wrong', 'Planning tickets', 'error'))
      .finally(() => setSaving(false))
  }

  return (
    <form id="planning_tickets_form" className="tk-action-form tk-bulk-form" onSubmit={submit}>
      <div className="form-group">
        <label htmlFor="planned_datetime" className="control-label">
          Planned intervention time <span className="required">*</span>
        </label>
        <input
          id="planned_datetime"
          className="form-control planned-datetimepicker"
          required
          type="datetime-local"
          value={when}
          onChange={(ev) => setWhen(ev.target.value)}
        />
      </div>
      <div className="form-group">
        <label htmlFor="suspend-comments" className="control-label">
          Reason for planning
        </label>
        <textarea id="suspend-comments" className="form-control" value={comments} onChange={(ev) => setComments(ev.target.value)} />
      </div>
      <hr />
      <TicketRows
        label="Selected tickets"
        rowClass="selected-ticket"
        buttonClass="remove-ticket"
        tickets={tickets}
        onRemove={(id) => setTickets((was) => was.filter((t) => t.ID !== id))}
        emptyText="Please select tickets for planning."
      />
      <Buttons saveClass="save-planning" disabled={saving || tickets.length === 0 || when === ''} onClose={onClose} />
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/*  ASSIGN TO OPERATOR - `assign_to_operator/form.php`                        */
/* -------------------------------------------------------------------------- */

function AssignForm({ form, onClose, onDone }: { form: BulkForm; onClose(): void; onDone(): void }) {
  const [tickets, setTickets] = useState<BulkFormTicket[]>(form.tickets)
  const assignees = form.assignees ?? []
  /* `<select required>` fara optiune goala: primul om e ales de la inceput. */
  const [assignee, setAssignee] = useState(assignees.length > 0 ? String(assignees[0].value) : '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (assignee === '' && assignees.length > 0) setAssignee(String(assignees[0].value))
  }, [assignee, assignees])

  const submit = (ev: FormEvent) => {
    ev.preventDefault()
    if (saving || tickets.length === 0 || assignee === '') return
    setSaving(true)
    assignTickets(
      tickets.map((t) => t.ID),
      Number(assignee),
    )
      .then((answer) => {
        toast(
          answer.skipped > 0 ? `${answer.assigned} assigned, ${answer.skipped} skipped.` : 'Tickets assigned successfully.',
          'Assign to operator',
          answer.skipped > 0 ? 'warning' : 'success',
        )
        onDone()
      })
      .catch((err) => toast(err instanceof Error ? err.message : 'Something went wrong', 'Assign to operator', 'error'))
      .finally(() => setSaving(false))
  }

  return (
    <form id="assign_tickets_form" className="tk-action-form tk-bulk-form" onSubmit={submit}>
      <Errors errors={form.errors} kind="warning" />
      <div className="form-group">
        <label htmlFor="assign-assignee" className="control-label">
          Assignee <span className="required">*</span>
        </label>
        <SelectPicker
          id="assign-assignee"
          className="form-control selected-assignee"
          title="Select..."
          options={assignees.map((a) => ({ value: String(a.value), label: a.name }))}
          value={assignee}
          onChange={setAssignee}
        />
      </div>
      <TicketRows
        label="Tickets"
        rowClass="ticket-to-assign"
        buttonClass="remove-assign-ticket"
        tickets={tickets}
        onRemove={(id) => setTickets((was) => was.filter((t) => t.ID !== id))}
        emptyText="No tickets available to perform this operation."
      />
      <Buttons saveClass="save-assign" disabled={saving || tickets.length === 0 || assignee === ''} onClose={onClose} />
    </form>
  )
}

/* -------------------------------------------------------------------------- */
/*  BUCATI COMUNE                                                              */
/* -------------------------------------------------------------------------- */

function Errors({ errors, kind = 'danger' }: { errors: string[]; kind?: 'danger' | 'warning' }) {
  if (errors.length === 0) return null

  return (
    <>
      {errors.map((error) => (
        <div className={`alert alert-${kind}`} key={error}>
          <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {error}
        </div>
      ))}
    </>
  )
}

/**
 * "Tickets" - cate un `input-group` pe tichet, cu numarul si subiectul in
 * casuta si butonul rosu de scos; cand nu mai ramane niciunul, textul din
 * `.tickets-to-merge-error` si fratii lui.
 */
function TicketRows({
  label,
  rowClass,
  buttonClass,
  tickets,
  onRemove,
  emptyText,
}: {
  label: string
  rowClass: string
  buttonClass: string
  tickets: BulkFormTicket[]
  onRemove(id: number): void
  emptyText: string
}) {
  return (
    <div className="form-group tk-bulk-tickets">
      <label className="control-label">{label}</label>
      <div>
        {tickets.map((ticket) => (
          <div className={`input-group ${rowClass}`} key={ticket.ID}>
            <input className="form-control input-sm" disabled placeholder="#ticket" type="text" value={`[#${ticket.ID}] ${ticket.subject}`} readOnly />
            <span className="input-group-btn">
              <button className={`btn btn-danger btn-sm ${buttonClass}`} type="button" title="Remove" onClick={() => onRemove(ticket.ID)}>
                <i className="fas fa-times" aria-hidden="true"></i>
              </button>
            </span>
          </div>
        ))}
        {tickets.length === 0 && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> {emptyText}
          </div>
        )}
      </div>
    </div>
  )
}

function Buttons({ saveClass, disabled, onClose }: { saveClass: string; disabled: boolean; onClose(): void }) {
  return (
    <div className="form-group align-right tk-bulk-buttons">
      <button type="button" className="btn btn-default" onClick={onClose}>
        Close
      </button>{' '}
      <button type="submit" className={`btn btn-primary ${saveClass}`} disabled={disabled}>
        Save
      </button>
    </div>
  )
}
